import React, { useState, useEffect, useCallback } from 'react';
import { Typography } from '@mui/material';
import { createLogger } from '../../utils/debugUtils';
import Step1_UploadList from './steps/Step1_UploadList';
import Step2_SelectColumn from './steps/Step2_SelectColumn';
import Step3_ResearchConfig from './steps/Step3_ResearchConfig';

const logger = createLogger('ResearchAgentWizard');
const STORAGE_KEY = 'research_wizard_state';

const stepTitles = [
  'UPLOAD YOUR LIST',
  'SELECT WEBSITE COLUMN',
  'RESEARCH CONFIGURATION'
];

export default function ResearchAgentWizard({ onSuccess, onStepChange, onGetNavigationButton }) {
  // Initial state - No persistence (matches List Cleaner behavior)
  const initialState = {
    // Step 1: Upload
    jobName: '',
    fileId: null,
    fileName: '',
    uploadedFile: null,
    rowCount: 0,
    columnCount: 0,
    columns: [],

    // Step 2: Select Column
    selectedColumn: null,
    columnPreview: [],

    // Step 3: Research Configuration
    researchInstructions: '',

    // Phase 3 fields (from template)
    enableQualification: true,
    enableCustomFields: false,
    customFields: [],
    enableWebSearch: false,
    webSearchInstructions: '',

    // Template tracking
    appliedTemplateId: null,
    appliedTemplateName: null
  };

  const [activeStep, setActiveStep] = useState(0);
  const [wizardState, setWizardState] = useState(initialState);
  const [loading, setLoading] = useState(false);

  // Navigation handlers - MUST be defined before useEffect that uses them
  const handleNext = useCallback(() => {
    setActiveStep((prevStep) => prevStep + 1);
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep((prevStep) => prevStep - 1);
  }, []);

  // Notify parent of step changes and provide navigation button
  useEffect(() => {
    if (onStepChange) {
      onStepChange(activeStep, stepTitles[activeStep]);
      logger.info('Step changed', {
        step: activeStep,
        title: stepTitles[activeStep]
      });
    }

    // Notify parent about navigation button needs
    if (onGetNavigationButton) {
      onGetNavigationButton({
        step: activeStep,
        hasNext: true, // Always show next button (will be "Begin Research" on final step)
        hasBack: activeStep > 0,
        onNext: handleNext,
        onBack: handleBack,
        wizardState
      });
    }
  }, [activeStep, onStepChange, onGetNavigationButton, handleNext, handleBack, wizardState]);

  const handleReset = useCallback(() => {
    setActiveStep(0);
    setWizardState({
      jobName: '',
      fileId: null,
      fileName: '',
      uploadedFile: null,
      rowCount: 0,
      columnCount: 0,
      columns: [],
      selectedColumn: null,
      columnPreview: [],
      researchInstructions: '',
      enableQualification: true,
      enableCustomFields: false,
      customFields: [],
      enableWebSearch: false,
      webSearchInstructions: '',
      appliedTemplateId: null,
      appliedTemplateName: null
    });
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const handleUpdateState = useCallback((updates) => {
    setWizardState((prevState) => ({
      ...prevState,
      ...updates
    }));
  }, []);

  // Render the appropriate step content
  const getStepContent = (step) => {
    const commonProps = {
      wizardState,
      onUpdateState: handleUpdateState,
      onNext: handleNext,
      onBack: handleBack,
      onReset: handleReset,
      loading,
      setLoading
    };

    switch (step) {
      case 0:
        return <Step1_UploadList {...commonProps} />;
      case 1:
        return <Step2_SelectColumn {...commonProps} />;
      case 2:
        return <Step3_ResearchConfig {...commonProps} onSuccess={onSuccess} />;
      default:
        return <Typography>Unknown step</Typography>;
    }
  };

  return getStepContent(activeStep);
}
